import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, AlertCircle, Stethoscope, ShieldCheck, ListChecks, Clock } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { BeforeAfterGallery } from "@/components/common/BeforeAfterGallery";
import { FaqSection } from "@/components/home/FaqSection";
import { Seo } from "@/components/common/Seo";
import { MedicalSources } from "@/components/common/MedicalSources";
import { buildBreadcrumbSchema, buildFaqSchema } from "@/lib/schema";
import { PROBLEMS, PROBLEM_CONTENT, TREATMENTS } from "@/data/site";
import { ToothIcon } from "@/components/common/ToothIcon";

export default function ProblemDetail() {
  const { slug } = useParams();
  const p = PROBLEMS.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/problems" replace />;

  const c = PROBLEM_CONTENT[slug] || {};
  const faqs = c.faqs || [];
  const treatments = p.treatments
    .map((s) => TREATMENTS.find((t) => t.slug === s))
    .filter(Boolean);

  const blocks = [
    { icon: AlertCircle, title: "Why it happens", text: p.causes },
    { icon: Stethoscope, title: "How we diagnose", text: "A thorough examination with digital imaging helps us pinpoint the exact cause and severity — so treatment is precise, not guesswork." },
    { icon: ShieldCheck, title: "Prevention", text: "Regular check-ups, professional cleaning and good home care dramatically reduce the risk. We'll build a simple routine that works for you." },
  ];

  return (
    <div data-testid="problem-detail-page">
      <Seo
        title={`${p.name} — Causes & Treatment | Dr. Ankesh Seth, Varanasi`}
        description={p.causes}
        path={`/problems/${p.slug}`}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Dental Problems", path: "/problems" },
            { name: p.name, path: `/problems/${p.slug}` },
          ]),
          ...(faqs.length ? [buildFaqSchema(faqs)] : []),
        ]}
      />
      <PageHero
        eyebrow="Dental Problem"
        titleLines={[p.name]}
        subtitle={p.short}
        crumbs={[{ label: "Dental Problems", to: "/problems" }, { label: p.name }]}
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-28 lg:px-20">
          {p.image && (
            <Reveal className="mb-14">
              <div className="aspect-[16/7] w-full overflow-hidden rounded-[2rem] border border-brand-line">
                <img src={p.image} alt={`${p.name} — causes and treatment in Varanasi`} width="1200" height="525" loading="eager" fetchPriority="high" className="h-full w-full object-cover" />
              </div>
            </Reveal>
          )}

          {c.intro && (
            <Reveal className="mb-14">
              <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">
                Understanding {p.name.toLowerCase()}
              </h2>
              {c.intro.map((para, i) => (
                <p key={i} className="mt-5 max-w-3xl text-lg leading-relaxed text-brand-muted">{para}</p>
              ))}

              {c.symptoms && (
                <div className="mt-10 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
                  <div>
                    <h3 className="flex items-center gap-2 font-display text-2xl font-medium text-brand-dark">
                      <ListChecks className="h-6 w-6 text-brand-blue" />
                      Symptoms to watch for
                    </h3>
                    <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                      {c.symptoms.map((s) => (
                        <li key={s} className="flex items-start gap-3 rounded-xl border border-brand-line bg-white p-4">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-blue" />
                          <span className="text-sm text-brand-dark">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {c.whenToSee && (
                    <div className="rounded-2xl border border-brand-blue/20 bg-brand-blue/[0.04] p-6">
                      <h3 className="flex items-center gap-2 font-display text-xl font-medium text-brand-dark">
                        <Clock className="h-5 w-5 text-brand-blue" />
                        When to see a dentist
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-brand-muted">{c.whenToSee}</p>
                    </div>
                  )}
                </div>
              )}
            </Reveal>
          )}

          <div className="grid gap-5 md:grid-cols-3">
            {blocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-brand-line bg-brand-soft p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                    <b.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-2xl font-medium text-brand-dark">{b.title}</h3>
                  <p className="mt-3 text-brand-muted">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">
              Recommended treatments
            </h2>
            <p className="mt-3 max-w-2xl text-brand-muted">
              Based on your symptoms, these treatments typically provide the best, longest-lasting
              relief. Book a consultation for a personalised plan.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {treatments.map((t, i) => (
              <Reveal key={t.slug} delay={i * 0.08}>
                <Link
                  to={`/${t.slug}`}
                  data-testid={`related-treatment-${t.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-brand-line bg-white p-7 transition-all hover:-translate-y-1 hover:border-brand-blue/40 hover:shadow-[0_30px_60px_-35px_rgba(2,125,199,0.5)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                    <ToothIcon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-medium text-brand-dark">{t.name}</h3>
                  <p className="mt-2 flex-1 text-sm text-brand-muted">{t.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
                    Explore
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <MedicalSources sources={c.sources} />
        </div>
      </section>

      {faqs.length > 0 && <FaqSection faqs={faqs} muted />}
      <BeforeAfterGallery muted />
      <CtaBand title={`Suffering from ${p.name.toLowerCase()}?`} text="Don't wait for it to get worse. Get fast, expert relief — call or WhatsApp Dr. Ankesh Seth's clinic today." />
    </div>
  );
}
